import { useEffect, useState } from "react";
import { Plus, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { slug } from "@/lib/geo";

const fmtPop = (p) => (p >= 1e6 ? `${(p / 1e6).toFixed(1)}M` : `${Math.round(p / 1000)}k`);

export const CityAutocomplete = ({ country, exclude, onPick }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.searchCities(country, q).then((r) => { setResults(r); setActive(r[0]?.fuzzy ? -1 : 0); }).catch(() => setResults([]));
    }, 180);
    return () => clearTimeout(t);
  }, [q, country]);
  const items = results.filter((c) => !exclude.includes(c.name));

  const pick = (c) => {
    onPick(c ? c.name : q.trim(), c ? { name: c.name, x: c.x, y: c.y } : null);
    setQ("");
    setResults([]);
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
    if (e.key === "Escape") setResults([]);
    if (e.key === "Enter") { e.preventDefault(); if (items[active]) pick(items[active]); else if (q.trim()) pick(null); }
  };

  return (
    <div className="relative mt-3">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Search for a city or town…" className="h-8 text-xs bg-white/5" data-testid="panel-custom-city-input" autoComplete="off" />
        <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => (items[active] ? pick(items[active]) : q.trim() && pick(null))} data-testid="panel-custom-city-add"><Plus size={14} /></Button>
      </div>
      {items.length > 0 && (
        <ul className="absolute z-20 left-0 right-10 mt-1 rounded-xl border border-white/10 bg-[#131A26] shadow-2xl overflow-hidden animate-pop" data-testid="city-suggestions">
          {items.map((c, i) => (
            <li key={`${c.name}-${c.x}-${c.y}`}>
              {c.fuzzy && !items[i - 1]?.fuzzy && <p className="px-3 pt-2 pb-1 font-mono text-[10px] uppercase tracking-[0.15em] text-sky-300/80" data-testid="city-did-you-mean">Did you mean</p>}
              <button type="button" onMouseEnter={() => setActive(i)} onClick={() => pick(c)} data-testid={`city-suggestion-${slug(c.name)}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs transition-colors ${i === active ? "bg-emerald-500/15 text-emerald-200" : "text-slate-300"}`}>
                <span className="flex items-center gap-2 min-w-0"><MapPin size={12} className="shrink-0 opacity-60" />
                  <span className="truncate">{c.name}{c.region && <span className="text-slate-500"> · {c.region}</span>}</span>
                </span>
                <span className="shrink-0 font-mono text-[10px] text-slate-500" data-testid={`city-suggestion-kind-${slug(c.name)}`}>{c.pop ? fmtPop(c.pop) : c.kind}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
